/* Brick Armory — reviews.
   <brick-reviews>     sort / star + photo filters / "Show more" over server-rendered
                       cards (snippets/review-card), photo lightbox.
   <brick-review-form> "Write a review": optional JSON endpoint (theme setting),
                       otherwise / on failure the native Shopify contact form. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const format = (template, values) =>
    String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in values ? values[key] : match));

  /* ---------------------------------------------------------------- list */
  if (!customElements.get('brick-reviews')) {
    class BrickReviews extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.list = this.querySelector('[data-list]');
        this.lightbox = this.querySelector('[data-lightbox]');
        this.bindLightbox();
        if (!this.list) return;

        this.items = Array.from(this.list.children);
        this.items.forEach((item, index) => (item.dataset.index = String(index)));
        this.perPage = Math.max(1, parseInt(this.dataset.perPage, 10) || 5);
        this.state = { sort: 'newest', stars: 0, photos: false, shown: this.perPage };

        try {
          const node = this.querySelector('[data-strings]');
          this.strings = node ? JSON.parse(node.textContent) : {};
        } catch (error) {
          this.strings = {};
        }

        this.status = this.querySelector('[data-status]');
        this.noMatch = this.querySelector('[data-no-match]');
        this.moreButton = this.querySelector('[data-more]');
        this.sortSelect = this.querySelector('[data-sort]');
        this.photosButton = this.querySelector('[data-filter-photos]');
        this.clearButton = this.querySelector('[data-clear-filter]');
        this.clearLabel = this.querySelector('[data-clear-label]');
        this.bars = Array.from(this.querySelectorAll('[data-filter-stars]'));

        const withPhotos = this.items.some((item) => item.hasAttribute('data-photo'));
        if (this.photosButton) this.photosButton.hidden = !withPhotos;
        if (!withPhotos && this.sortSelect) {
          const option = this.sortSelect.querySelector('option[value="photos"]');
          if (option) option.remove();
        }

        this.bars.forEach((bar) =>
          bar.addEventListener('click', () => {
            const stars = parseInt(bar.dataset.filterStars, 10);
            this.update({ stars: this.state.stars === stars ? 0 : stars, shown: this.perPage });
          })
        );
        if (this.photosButton) {
          this.photosButton.addEventListener('click', () =>
            this.update({ photos: !this.state.photos, shown: this.perPage })
          );
        }
        if (this.clearButton) {
          this.clearButton.addEventListener('click', () => {
            this.update({ stars: 0, shown: this.perPage });
            const bar = this.bars.find((b) => !b.disabled);
            if (bar) bar.focus();
          });
        }
        if (this.sortSelect) {
          this.sortSelect.addEventListener('change', () =>
            this.update({ sort: this.sortSelect.value, shown: this.perPage })
          );
        }
        if (this.moreButton) {
          this.moreButton.addEventListener('click', () => {
            const firstNew = this.state.shown;
            this.update({ shown: this.state.shown + this.perPage }, { announce: false });
            const target = this.current[firstNew];
            if (target) {
              target.setAttribute('tabindex', '-1');
              target.focus({ preventScroll: false });
            }
          });
        }

        this.update({}, { announce: false });
      }

      sorted() {
        const { sort, stars, photos } = this.state;
        const byNewest = (a, b) =>
          (b.dataset.date || '').localeCompare(a.dataset.date || '') ||
          Number(a.dataset.index) - Number(b.dataset.index);
        const rating = (el) => Number(el.dataset.rating) || 0;
        const photo = (el) => (el.hasAttribute('data-photo') ? 1 : 0);
        const result = this.items.filter(
          (el) => (!stars || rating(el) === stars) && (!photos || el.hasAttribute('data-photo'))
        );
        if (sort === 'highest') result.sort((a, b) => rating(b) - rating(a) || byNewest(a, b));
        else if (sort === 'lowest') result.sort((a, b) => rating(a) - rating(b) || byNewest(a, b));
        else if (sort === 'photos') result.sort((a, b) => photo(b) - photo(a) || byNewest(a, b));
        else result.sort(byNewest);
        return result;
      }

      update(patch, { announce = true } = {}) {
        Object.assign(this.state, patch);
        const { stars, photos, shown } = this.state;
        this.current = this.sorted();

        const visible = new Set(this.current.slice(0, shown));
        this.items.forEach((el) => {
          el.hidden = !visible.has(el);
        });
        // Re-order the DOM to the sorted order (keeps reading order = visual order).
        this.current.forEach((el) => this.list.append(el));
        this.items.filter((el) => !this.current.includes(el)).forEach((el) => this.list.append(el));

        this.bars.forEach((bar) => bar.setAttribute('aria-pressed', String(Number(bar.dataset.filterStars) === stars)));
        if (this.photosButton) this.photosButton.setAttribute('aria-pressed', String(photos));
        if (this.clearButton) {
          this.clearButton.hidden = !stars;
          if (this.clearLabel) this.clearLabel.textContent = format(this.strings.starsFilter, { stars });
        }
        if (this.moreButton) this.moreButton.hidden = this.current.length <= shown;
        if (this.noMatch) this.noMatch.hidden = this.current.length > 0;
        this.dataset.filtered = String(Boolean(stars || photos));

        if (this.status && announce) {
          this.status.textContent = format(this.strings.status, {
            shown: Math.min(shown, this.current.length),
            count: this.current.length,
          });
        }
      }

      /* Photo lightbox (also used by the home Reviews section) */
      bindLightbox() {
        if (!this.lightbox) return;
        const img = this.lightbox.querySelector('[data-lightbox-img]');
        const caption = this.lightbox.querySelector('[data-lightbox-caption]');
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-review-photo]');
          if (!link || !this.contains(link) || typeof this.lightbox.showModal !== 'function') return;
          event.preventDefault();
          const thumb = link.querySelector('img');
          img.src = link.getAttribute('href');
          img.alt = thumb ? thumb.alt : '';
          if (caption) caption.textContent = thumb ? thumb.alt : '';
          this.opener = link;
          this.lightbox.showModal();
          const close = this.lightbox.querySelector('[data-lightbox-close]');
          if (close) close.focus();
        });
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox || event.target.closest('[data-lightbox-close]')) this.lightbox.close();
        });
        this.lightbox.addEventListener('close', () => {
          img.src = 'data:,';
          if (this.opener) this.opener.focus({ preventScroll: true });
        });
      }
    }
    customElements.define('brick-reviews', BrickReviews);
  }

  /* ---------------------------------------------------------------- form */
  if (!customElements.get('brick-review-form')) {
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    class BrickReviewForm extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.form = this.querySelector('form');
        this.details = this.querySelector('[data-details]');
        this.error = this.querySelector('[data-error]');
        this.success = this.querySelector('[data-success]');
        this.submitButton = this.querySelector('[data-submit]');
        if (!this.form) return;

        // "Write a review" links anywhere on the page open + focus the form.
        this.onOpenLink = (event) => {
          const link = event.target.closest('[data-open-review-form]');
          if (!link || link.getAttribute('href') !== `#${this.id}`) return;
          event.preventDefault();
          this.open();
        };
        document.addEventListener('click', this.onOpenLink);
        if (window.location.hash === `#${this.id}`) this.open({ scroll: false });

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.form.addEventListener('input', () => this.clearError());
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOpenLink);
        this.bound = false;
      }

      open({ scroll = true } = {}) {
        if (this.details) this.details.open = true;
        if (scroll) this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        const first = this.form.querySelector('input[type="radio"]');
        if (first) setTimeout(() => first.focus({ preventScroll: true }), reducedMotion() ? 0 : 320);
      }

      showError(text, field) {
        if (!this.error) return;
        this.error.textContent = text;
        this.error.hidden = false;
        if (field) {
          field.setAttribute('aria-invalid', 'true');
          field.focus();
        }
      }

      clearError() {
        if (this.error) this.error.hidden = true;
        this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
      }

      validate() {
        const data = new FormData(this.form);
        if (!data.get('contact[Rating]')) {
          this.showError(this.dataset.errorRating, this.form.querySelector('input[type="radio"]'));
          return null;
        }
        const required = Array.from(this.form.querySelectorAll('[required]:not([type="radio"])'));
        const missing = required.find((el) => (el.type === 'checkbox' ? !el.checked : !String(el.value).trim()));
        if (missing) {
          this.showError(this.dataset.errorRequired, missing);
          return null;
        }
        const email = this.form.querySelector('input[type="email"]');
        if (email && !EMAIL_RE.test(email.value.trim())) {
          this.showError(this.dataset.errorEmail, email);
          return null;
        }
        return data;
      }

      async onSubmit(event) {
        const endpoint = this.dataset.endpoint;
        const hp = this.form.querySelector('[data-hp]');
        // No endpoint: the browser's native validation + Shopify contact POST.
        if (!endpoint && !(hp && hp.value)) return;

        event.preventDefault();
        if (this.busy) return;
        this.clearError();
        const data = this.validate();
        if (!data) return;

        if (hp && hp.value) return this.done(); // bot: pretend success, send nothing

        this.busy = true;
        const label = this.submitButton ? this.submitButton.textContent : '';
        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.textContent = this.dataset.sending || label;
        }

        const payload = {
          type: 'brick_review',
          status: 'pending',
          product_id: this.dataset.productId,
          product_handle: this.dataset.productHandle,
          rating: Number(data.get('contact[Rating]')),
          title: String(data.get('contact[Review title]') || '').trim(),
          body: String(data.get('contact[body]') || '').trim(),
          author: String(data.get('contact[name]') || '').trim(),
          email: String(data.get('contact[email]') || '').trim(),
          locale: this.dataset.locale || document.documentElement.lang || '',
          page: window.location.href,
        };

        let ok = false;
        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
          ok = res.ok;
        } catch (error) {
          ok = false;
        }

        this.busy = false;
        if (this.submitButton) {
          this.submitButton.disabled = false;
          this.submitButton.textContent = label;
        }
        if (ok) return this.done();
        // Fallback: native Shopify contact form (store inbox).
        HTMLFormElement.prototype.submit.call(this.form);
      }

      done() {
        this.form.reset();
        if (this.details) this.details.open = false;
        if (this.success) {
          this.success.hidden = false;
          this.success.focus();
        }
      }
    }
    customElements.define('brick-review-form', BrickReviewForm);
  }
})();
